import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Typography, CircularProgress } from "@mui/material";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import GroupRoundedIcon from "@mui/icons-material/GroupRounded";
import SavingsRoundedIcon from "@mui/icons-material/SavingsRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";

import { brand, fonts } from "../../themes/theme";
import logoBlue from "../../assets/mential-logo-blue.png";
import tenants from "../../data/tenants.json";
import BrandMark from "../../components/layout/BrandMark";
import { applyTenantLanguage } from "../../services/i18n";

// Iconos fijos de las 3 features del hero; las etiquetas vienen del tipo de
// organización elegido (tenants.json), así el hero se adapta al tenant.
const FEATURE_ICONS = [InsightsRoundedIcon, GroupRoundedIcon, SavingsRoundedIcon];

const inputSx = {
    width: "100%",
    p: "12px 16px",
    bgcolor: "#fff",
    border: "1px solid",
    borderColor: brand.borderLight,
    borderRadius: "8px",
    fontSize: 14,
    lineHeight: 1.25,
    color: brand.navy,
    fontFamily: fonts.body,
    boxSizing: "border-box",
    outline: "none",
    "&:focus": {
        borderColor: brand.primary,
        boxShadow: "0 0 0 2px rgba(50,72,202,0.2)",
    },
};

// Desplegable compacto para el selector de organización (secundario, ligero).
const compactSelectSx = {
    flex: 1,
    minWidth: 0,
    p: "8px 10px",
    bgcolor: "#fff",
    border: "1px solid",
    borderColor: brand.borderLight,
    borderRadius: "8px",
    fontSize: 12.5,
    color: brand.navy,
    fontFamily: fonts.body,
    boxSizing: "border-box",
    cursor: "pointer",
    outline: "none",
    "&:focus": { borderColor: brand.primary },
};

/**
 * Duplicado visual del login del psicólogo (mential-webapp, /login-practitioner),
 * con el texto adaptado a esta demo (dirección clínica). Solo front: sin
 * Firebase/Redux/analítica. Cualquier dato enviado "funciona" (no hay
 * validación) — sirve solo para ver el diseño del login. Ruta: /login.
 */
function LoginPractitionerView() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [mode, setMode] = useState("signin"); // signin | forgot
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [recovered, setRecovered] = useState(false);
    // Selector de tenant (demo multi-tenant): tipo (clínica/farma) + organización.
    const [orgType, setOrgType] = useState(tenants.types[0].key);
    const [org, setOrg] = useState(tenants.types[0].orgs[0]);
    const currentType = tenants.types.find((x) => x.key === orgType) || tenants.types[0];

    const fakeLogin = () => {
        setLoading(true);
        setTimeout(() => {
            sessionStorage.setItem("demoLoggedIn", "true");
            sessionStorage.setItem("demoOrgType", orgType);
            sessionStorage.setItem("demoOrg", org);
            applyTenantLanguage(orgType);
            navigate("/");
        }, 650);
    };

    const handleRecover = () => {
        if (!recovered) {
            setLoading(true);
            setTimeout(() => {
                setLoading(false);
                setRecovered(true);
            }, 650);
        } else {
            setMode("signin");
            setRecovered(false);
        }
    };

    return (
        <Box sx={{ position: "fixed", inset: 0, overflow: "hidden", display: "flex", flexDirection: "column", bgcolor: "#fff" }}>
            {mode === "signin" ? (
                <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: { xs: "column", md: "row" }, overflow: "hidden" }}>
                    {/* Panel izquierdo — hero (oculto en movil) */}
                    <Box
                        sx={{
                            display: { xs: "none", md: "flex" },
                            width: "50%",
                            flex: "0 0 50%",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            p: "clamp(24px, 3vw, 48px)",
                            bgcolor: brand.navy,
                            position: "relative",
                            overflow: "hidden",
                            "&::after": {
                                content: '""',
                                position: "absolute",
                                bottom: 0,
                                right: -50,
                                width: 200,
                                height: "100%",
                                bgcolor: brand.blueSlate,
                                borderRadius: "100px 0 0 0",
                                opacity: 0.3,
                            },
                            "&::before": {
                                content: '""',
                                position: "absolute",
                                top: "50%",
                                right: -100,
                                width: 300,
                                height: 300,
                                bgcolor: brand.blueSlate,
                                borderRadius: "50%",
                                opacity: 0.2,
                            },
                        }}
                    >
                        <Box sx={{ position: "relative", zIndex: 10, maxWidth: 448 }}>
                            <Box sx={{ mb: 6 }}>
                                <BrandMark name={org} size="lg" />
                            </Box>
                            <Typography sx={{ fontFamily: fonts.display, fontSize: "2.25rem", mb: 2, color: "#fff", lineHeight: 1.2 }}>
                                {t("login.heroTitle")}
                            </Typography>
                            <Typography sx={{ fontSize: 18, color: "rgba(255,255,255,0.8)", lineHeight: 1.6 }}>
                                {currentType.hero.subtitle.replace("{org}", org)}
                            </Typography>
                            <Box sx={{ mt: 5 }}>
                                {currentType.hero.features.map((text, i) => {
                                    const Icon = FEATURE_ICONS[i] || InsightsRoundedIcon;
                                    return (
                                    <Box key={text} sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                                        <Box
                                            sx={{
                                                width: 40,
                                                height: 40,
                                                borderRadius: "8px",
                                                bgcolor: "rgba(255,255,255,0.1)",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                flexShrink: 0,
                                            }}
                                        >
                                            <Icon sx={{ color: "#fff", fontSize: 20 }} />
                                        </Box>
                                        <Typography sx={{ color: "rgba(255,255,255,0.9)", fontSize: 14.5 }}>{text}</Typography>
                                    </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                    </Box>

                    {/* Panel derecho — formulario */}
                    <Box
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            bgcolor: "#fff",
                            p: "clamp(16px, 4vw, 24px)",
                            overflowY: "auto",
                        }}
                    >
                        <Box sx={{ width: "100%", maxWidth: 448, py: "clamp(12px, 2vh, 24px)" }}>
                            {/* Logo movil */}
                            <Box sx={{ display: { xs: "flex", md: "none" }, justifyContent: "center", mb: 4 }}>
                                <Box component="img" src={logoBlue} alt="Mential" sx={{ height: 36 }} />
                            </Box>

                            <Box sx={{ mb: 4 }}>
                                <Typography sx={{ fontFamily: fonts.display, fontSize: "clamp(1.25rem, 2.5vw, 1.75rem)", color: brand.navy, mb: 1 }}>
                                    {t("login.signinTitle")}
                                </Typography>
                                <Typography sx={{ fontSize: 12, color: brand.blueSlate }}>{t("login.signinSubtitle")}</Typography>
                            </Box>

                            <Box>
                                <Box sx={{ mb: "clamp(12px, 2vh, 20px)" }}>
                                    <Typography component="label" sx={{ display: "block", fontSize: 14, fontWeight: 500, color: brand.navy, mb: 1 }}>
                                        {t("login.user")}
                                    </Typography>
                                    <Box
                                        component="input"
                                        type="text"
                                        name="demo-user"
                                        autoComplete="off"
                                        data-1p-ignore
                                        data-lpignore="true"
                                        data-form-type="other"
                                        placeholder={t("login.userPlaceholder")}
                                        sx={inputSx}
                                    />
                                </Box>

                                <Box sx={{ mb: "clamp(12px, 2vh, 16px)" }}>
                                    <Typography component="label" sx={{ display: "block", fontSize: 14, fontWeight: 500, color: brand.navy, mb: 1 }}>
                                        {t("login.password")}
                                    </Typography>
                                    <Box sx={{ position: "relative" }}>
                                        <Box
                                            component="input"
                                            type="text"
                                            name="demo-pass"
                                            autoComplete="off"
                                            data-1p-ignore
                                            data-lpignore="true"
                                            data-form-type="other"
                                            placeholder={t("login.passwordPlaceholder")}
                                            sx={{ ...inputSx, pr: 6, WebkitTextSecurity: showPassword ? "none" : "disc" }}
                                        />
                                        <Box
                                            component="button"
                                            type="button"
                                            onClick={() => setShowPassword((v) => !v)}
                                            aria-label={showPassword ? t("login.hidePassword") : t("login.showPassword")}
                                            sx={{
                                                position: "absolute",
                                                right: 16,
                                                top: "50%",
                                                transform: "translateY(-50%)",
                                                border: "none",
                                                bgcolor: "transparent",
                                                cursor: "pointer",
                                                color: brand.blueSlate,
                                                p: 0,
                                                display: "flex",
                                            }}
                                        >
                                            {showPassword ? <VisibilityOffRoundedIcon sx={{ fontSize: 20 }} /> : <VisibilityRoundedIcon sx={{ fontSize: 20 }} />}
                                        </Box>
                                    </Box>
                                </Box>

                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "clamp(16px, 2.5vh, 24px)" }}>
                                    <Box component="label" sx={{ display: "flex", alignItems: "center", gap: 1, cursor: "pointer" }}>
                                        <Box
                                            component="input"
                                            type="checkbox"
                                            sx={{ width: 16, height: 16, borderRadius: "4px", border: "1px solid", borderColor: brand.borderLight, accentColor: brand.primary }}
                                        />
                                        <Typography sx={{ fontSize: 12, color: brand.blueSlate }}>{t("login.remember")}</Typography>
                                    </Box>
                                    <Box
                                        component="button"
                                        type="button"
                                        onClick={() => setMode("forgot")}
                                        sx={{ border: "none", bgcolor: "transparent", cursor: "pointer", fontFamily: fonts.body, fontSize: 14, color: brand.primary, p: 0 }}
                                    >
                                        {t("login.forgotLink")}
                                    </Box>
                                </Box>

                                <Box
                                    component="button"
                                    type="button"
                                    onClick={fakeLogin}
                                    disabled={loading}
                                    sx={{
                                        width: "100%",
                                        p: "14px",
                                        bgcolor: brand.primary,
                                        color: "#fff",
                                        fontWeight: 500,
                                        borderRadius: "8px",
                                        border: "none",
                                        cursor: loading ? "not-allowed" : "pointer",
                                        opacity: loading ? 0.75 : 1,
                                        fontFamily: fonts.body,
                                        fontSize: 14,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: 1,
                                    }}
                                >
                                    {loading && <CircularProgress size={16} sx={{ color: "#fff" }} />}
                                    {t("login.signinTitle")}
                                </Box>

                                {/* Selector de organización (demo multi-tenant) — secundario, ligero */}
                                <Box sx={{ mt: "clamp(12px, 2vh, 16px)" }}>
                                    <Typography sx={{ fontSize: 11, color: brand.blueSlate, mb: 0.75 }}>{t("login.accessAs")}</Typography>
                                    <Box sx={{ display: "flex", gap: 1 }}>
                                        <Box
                                            component="select"
                                            aria-label={t("login.orgType")}
                                            value={orgType}
                                            onChange={(e) => {
                                                const next = tenants.types.find((x) => x.key === e.target.value) || tenants.types[0];
                                                setOrgType(next.key);
                                                setOrg(next.orgs[0]);
                                            }}
                                            sx={compactSelectSx}
                                        >
                                            {tenants.types.map((tp) => (
                                                <option key={tp.key} value={tp.key}>
                                                    {tp.label}
                                                </option>
                                            ))}
                                        </Box>
                                        <Box
                                            component="select"
                                            aria-label={t("login.org")}
                                            value={org}
                                            onChange={(e) => setOrg(e.target.value)}
                                            sx={compactSelectSx}
                                        >
                                            {currentType.orgs.map((o) => (
                                                <option key={o} value={o}>
                                                    {o}
                                                </option>
                                            ))}
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>

                            <Box sx={{ display: "flex", alignItems: "center", gap: 2, my: "clamp(12px, 2vh, 24px)" }}>
                                <Box sx={{ flex: 1, height: "1px", bgcolor: brand.borderLight }} />
                                <Typography sx={{ fontSize: 11, color: brand.blueSlate }}>{t("login.orAlso")}</Typography>
                                <Box sx={{ flex: 1, height: "1px", bgcolor: brand.borderLight }} />
                            </Box>

                            <Box
                                component="button"
                                type="button"
                                onClick={fakeLogin}
                                disabled={loading}
                                sx={{
                                    width: "100%",
                                    p: "14px",
                                    bgcolor: brand.navy,
                                    color: "#fff",
                                    fontWeight: 500,
                                    borderRadius: "8px",
                                    border: "none",
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 1.5,
                                    fontFamily: fonts.body,
                                    fontSize: 14,
                                }}
                            >
                                <Box component="svg" viewBox="0 0 24 24" sx={{ width: 20, height: 20 }}>
                                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                                </Box>
                                {t("login.google")}
                            </Box>

                            <Typography sx={{ fontSize: 11, color: brand.blueSlate, textAlign: "center", mt: "clamp(16px, 3vh, 32px)" }}>
                                {t("login.noAccount")}{" "}
                                <Box component="a" href="https://mential.io" target="_blank" rel="noopener noreferrer" sx={{ color: brand.primary }}>
                                    {t("login.contact")}
                                </Box>
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            ) : (
                /* Recuperar contraseña */
                <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", p: 3, bgcolor: "#fff", overflowY: "auto" }}>
                    <Box sx={{ width: "100%", maxWidth: 448 }}>
                        <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
                            <Box component="img" src={logoBlue} alt="Mential" sx={{ height: 40 }} />
                        </Box>
                        <Typography sx={{ fontFamily: fonts.display, fontSize: "1.5rem", color: brand.navy, mb: 1, textAlign: "center" }}>
                            {t("login.forgotTitle")}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: brand.blueSlate, mb: 3, textAlign: "center" }}>
                            {t("login.forgotSubtitle")}
                        </Typography>

                        {!recovered && (
                            <Box sx={{ mb: 2.5 }}>
                                <Typography component="label" sx={{ display: "block", fontSize: 14, fontWeight: 500, color: brand.navy, mb: 1 }}>
                                    {t("login.email")}
                                </Typography>
                                <Box component="input" type="text" name="demo-recover" autoComplete="off" data-1p-ignore data-lpignore="true" data-form-type="other" placeholder={t("login.emailPlaceholder")} sx={inputSx} />
                            </Box>
                        )}

                        {recovered && (
                            <Typography sx={{ fontSize: 12, color: brand.blueSlate, mb: 2 }}>
                                {t("login.recoverSent")}
                            </Typography>
                        )}

                        <Box
                            component="button"
                            type="button"
                            onClick={handleRecover}
                            disabled={loading}
                            sx={{
                                width: "100%",
                                p: "14px",
                                bgcolor: brand.primary,
                                color: "#fff",
                                fontWeight: 500,
                                borderRadius: "8px",
                                border: "none",
                                cursor: "pointer",
                                fontFamily: fonts.body,
                                fontSize: 14,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 1,
                            }}
                        >
                            {loading && <CircularProgress size={16} sx={{ color: "#fff" }} />}
                            {recovered ? t("login.back") : t("login.recover")}
                        </Box>

                        <Typography sx={{ textAlign: "center", mt: 3 }}>
                            <Box
                                component="button"
                                type="button"
                                onClick={() => {
                                    setMode("signin");
                                    setRecovered(false);
                                }}
                                sx={{ border: "none", bgcolor: "transparent", cursor: "pointer", fontFamily: fonts.body, fontSize: 14, color: brand.primary, p: 0 }}
                            >
                                {t("login.backToSignin")}
                            </Box>
                        </Typography>
                    </Box>
                </Box>
            )}
        </Box>
    );
}

export default LoginPractitionerView;
